// MSG91 OTP Widget (SendOTP Web SDK) — loads otp-provider.js once, initialises
// with the public widget credentials, and wraps the exposed methods as promises.
const WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID;
const TOKEN_AUTH = process.env.REACT_APP_MSG91_TOKEN_AUTH;

export const msg91Enabled = Boolean(WIDGET_ID && TOKEN_AUTH);

let loadPromise = null;
// The most recent verified access token — MSG91 returns it via either the
// method-level callback or the widget's global success callback.
let lastToken = "";

// MSG91 access tokens are JWTs (contain dots). Guard against treating a short
// status string or a send-OTP reqId as a token.
const isLikelyToken = (v) => typeof v === "string" && v.includes(".") && v.length > 20;

const pickToken = (data) => {
  if (!data || typeof data !== "object") return "";
  return (
    data["access-token"] ||
    data.accessToken ||
    data.token ||
    (isLikelyToken(data.message) ? data.message : "") ||
    ""
  );
};

export function resetMsg91Token() {
  lastToken = "";
}

export function loadMsg91() {
  if (!msg91Enabled) return Promise.reject(new Error("Verification is temporarily unavailable."));
  if (window.sendOtp && window.verifyOtp) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://verify.msg91.com/otp-provider.js";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => { loadPromise = null; reject(new Error("Couldn't load the verification service. Please refresh and try again.")); };
      document.head.appendChild(s);
    }).then(() => {
      if (typeof window.initSendOTP === "function") {
        window.initSendOTP({
          widgetId: WIDGET_ID,
          tokenAuth: TOKEN_AUTH,
          exposeMethods: true,
          success: (data) => { const t = pickToken(data); if (t) lastToken = t; },
          failure: () => {},
        });
      }
    });
  }
  return loadPromise;
}

// Normalize a user-typed phone to MSISDN (country code, no '+'). Defaults to India (91).
export function toMsisdn(raw, cc = "91") {
  if (!raw) return "";
  let s = String(raw).replace(/[^\d+]/g, "");
  if (s.startsWith("+")) return s.slice(1);
  if (s.startsWith("00")) return s.slice(2);
  s = s.replace(/^0+/, "");
  if (s.length === 10) return `${cc}${s}`;
  return s;
}

const reqIdFrom = (data) => data?.reqId || data?.reqid || data?.message || "";

export function sendOtp(identifier) {
  return new Promise((resolve, reject) => {
    try {
      window.sendOtp(identifier, (data) => resolve(reqIdFrom(data)), (err) => reject(err));
    } catch (e) { reject(e); }
  });
}

export function retryOtp(channel, reqId) {
  return new Promise((resolve, reject) => {
    try {
      window.retryOtp(channel, (data) => resolve(data), (err) => reject(err), reqId);
    } catch (e) { reject(e); }
  });
}

export function verifyOtp(otp, reqId) {
  return new Promise((resolve, reject) => {
    try {
      window.verifyOtp(otp, (data) => {
        const token = pickToken(data) || lastToken;
        if (token) { lastToken = token; resolve(token); }
        else reject(new Error(data?.message || "OTP verification failed."));
      }, (err) => {
        // "Already verified" is a success in disguise — MSG91 verified this
        // session earlier; reuse the captured token instead of failing.
        const m = String(err?.message || err?.msg || err || "").toLowerCase();
        if (lastToken && m.includes("alread")) return resolve(lastToken);
        reject(err);
      }, reqId);
    } catch (e) { reject(e); }
  });
}

export function msg91ErrorMessage(err) {
  const msg = err?.message || err?.msg || (typeof err === "string" ? err : "");
  return msg || "Something went wrong during verification. Please try again.";
}
